import React, { useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, Search, Pencil, Trash2, ChevronUp, ChevronDown, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { adminAPI } from '../../services/api';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';
import TikTokEventsAPIFormModal from '../../components/admin/TikTokEventsAPIFormModal';

const TikTokEventsAPIManagement: React.FC = () => {
  const [configs, setConfigs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [testingId, setTestingId] = useState<number | null>(null);

  useEffect(() => { loadConfigs(); }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getTikTokEventsAPI();
      let data = response.data || response;
      if (data?.results && Array.isArray(data.results)) data = data.results;
      setConfigs(Array.isArray(data) ? data : data ? [data] : []);
    } catch (err) { console.error('Failed:', err); setConfigs([]); }
    finally { setLoading(false); }
  };

  const handleTest = async (id: number) => {
    setTestingId(id);
    try {
      const res = await adminAPI.testTikTokEventsAPI(id);
      alert('Test event sent! Check TikTok Ads Manager.');
    } catch (err: any) { alert('Test failed. Check credentials.'); }
    finally { setTestingId(null); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this config?')) return;
    try { await adminAPI.deleteTikTokEventsAPI(id); await loadConfigs(); }
    catch (err: any) { alert('Failed to delete'); }
  };

  const handleSubmit = async (data: any) => {
    try {
      setSubmitting(true);
      if (editingConfig) await adminAPI.updateTikTokEventsAPI(editingConfig.id, data);
      else await adminAPI.createTikTokEventsAPI(data);
      setShowModal(false); setEditingConfig(null); await loadConfigs();
    } catch (err: any) { alert('Failed to save'); } finally { setSubmitting(false); }
  };

  const handleSort = (f: string) => { if (sortField === f) setSortDir(d => d==='asc'?'desc':'asc'); else { setSortField(f); setSortDir('asc'); } setCurrentPage(1); };
  const SA = ({ field }: { field: string }) => sortField !== field ? null : sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;
  const fmtDate = (v: string) => v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const filtered = useMemo(() => {
    let list = configs.filter(c => [c.pixel_id, c.name].some(v => v?.toLowerCase().includes(searchTerm.toLowerCase())));
    list = [...list].sort((a: any, b: any) => sortDir === 'asc' ? String(a[sortField]??'').localeCompare(String(b[sortField]??'')) : String(b[sortField]??'').localeCompare(String(a[sortField]??'')));
    return list;
  }, [configs, searchTerm, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">TikTok Events API</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-add" onClick={() => { setEditingConfig(null); setShowModal(true); }}>
            <Plus size={13} /> Add Config <span className="adm-btn-badge">{configs.length}</span>
          </button>
          <button className="adm-btn adm-btn-refresh" onClick={loadConfigs} disabled={loading}>
            {loading ? <span className="adm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left"><span className="adm-count"><strong>{filtered.length}</strong> config(s)</span></div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search…" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>
      <div className="adm-table-card">
        <div className="adm-table-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('pixel_id')}>Pixel ID <SA field="pixel_id" /></th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('name')}>Name <SA field="name" /></th>
                <th className="adm-th">Access Token</th>
                <th className="adm-th">Mode</th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('is_active')}>Status <SA field="is_active" /></th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('created_at')}>Created <SA field="created_at" /></th>
                <th className="adm-th" style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading…</td></tr>}
              {!loading && paginated.length === 0 && <tr><td colSpan={7} className="adm-empty">No configs found.</td></tr>}
              {!loading && paginated.map((c: any) => (
                <tr className="adm-row" key={c.id}>
                  <td className="adm-td"><span className="adm-mono">{c.pixel_id}</span></td>
                  <td className="adm-td"><span className="adm-name-main">{c.name || '—'}</span></td>
                  <td className="adm-td"><span className="adm-mono" style={{ color: '#888' }}>{c.access_token_masked || '—'}</span></td>
                  <td className="adm-td"><span className={'adm-chip ' + (c.test_event_code ? 'adm-chip-amber' : 'adm-chip-green')}>{c.test_event_code ? 'Test' : 'Production'}</span></td>
                  <td className="adm-td"><span className={'adm-chip ' + (c.is_active ? 'adm-chip-green' : 'adm-chip-red')}>{c.is_active ? <><CheckCircle2 size={9} /> Active</> : <><XCircle size={9} /> Inactive</>}</span></td>
                  <td className="adm-td"><span className="adm-date">{fmtDate(c.created_at)}</span></td>
                  <td className="adm-td" style={{ padding: '6px 4px' }}>
                    <div className="adm-actions-row">
                      <button className="adm-action-btn adm-ab-approve" style={{ fontSize: 10, padding: '0 6px', width: 'auto', minWidth: 36 }} onClick={() => handleTest(c.id)} disabled={testingId === c.id}>{testingId === c.id ? <span className="adm-spin"><Loader2 size={10} /></span> : 'Test'}</button>
                      <button className="adm-action-btn adm-ab-edit" onClick={() => { setEditingConfig(c); setShowModal(true); }}><Pencil size={12} /></button>
                      <button className="adm-action-btn adm-ab-del" onClick={() => handleDelete(c.id)}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="adm-pagination">
          <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10,20,50].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
          <div className="adm-page-info">{filtered.length === 0 ? '0 configs' : `${(currentPage-1)*pageSize+1}–${Math.min(currentPage*pageSize,filtered.length)} of ${filtered.length}`}</div>
          <div className="adm-page-controls">
            <button className="adm-page-btn" disabled={currentPage===1} onClick={() => setCurrentPage(p => p-1)}>‹</button>
            <button className="adm-page-btn" disabled={currentPage===totalPages} onClick={() => setCurrentPage(p => p+1)}>›</button>
          </div>
        </div>
      </div>
      <TikTokEventsAPIFormModal isOpen={showModal} onClose={() => { setShowModal(false); setEditingConfig(null); }} onSubmit={handleSubmit} initialData={editingConfig} loading={submitting} />
    </div>
  );
};

export default TikTokEventsAPIManagement;